import React, { useState, useEffect } from 'react';
import { Box, Grid } from '@material-ui/core';
import { v4 as uuidv4 } from 'uuid';
import { TextButton } from '@ds';
import { KeyValueListItem } from './KeyValueListItem';
import classNames from './CustomizeOrganizationSettings.module.scss';

type Field = {
  id: string;
  secretName: string;
  secretValue: string;
};

type KeyValueListProps = {
  keyValueFields?: Field[];
  onFieldsChange: (fields: Field[]) => void;
  maxItemsAllowed: number;
  baseNames: string[];
  initialKeyValues: Field[];
  handleInitialKeyValues: (keyValues: Field[]) => void;
};

const getInitialFields = (fieldsData: Field[]): Field[] => {
  return fieldsData.map(field => ({
    ...field,
    id: field.id || uuidv4()
  }));
};

const emptyFields = [{ id: uuidv4(), secretName: '', secretValue: '' }];

export const STARS_VALUE = '************';

export const KeyValueList: React.FC<KeyValueListProps> = ({
  keyValueFields,
  onFieldsChange,
  maxItemsAllowed,
  baseNames,
  initialKeyValues,
  handleInitialKeyValues
}) => {
  const [fields, setFields] = useState<Field[]>([
    { id: uuidv4(), secretName: '', secretValue: '' }
  ]);
  const hasEmptyField = fields.some(
    field => !field.secretName || !field.secretValue
  );
  const isMaxReached = fields.length >= maxItemsAllowed;
  const initialIds = initialKeyValues.map(({ id }) => id);

  useEffect(() => {
    if (baseNames?.length > 0) {
      const mappedNames = baseNames.map(name => ({
        secretName: name,
        secretValue: STARS_VALUE,
        id: uuidv4()
      }));
      onFieldsChange(mappedNames);
      handleInitialKeyValues(mappedNames);
    } else {
      onFieldsChange([]);
      handleInitialKeyValues([]);
    }

    return () => {
      onFieldsChange([]);
      handleInitialKeyValues([]);
    };
  }, [baseNames, onFieldsChange, handleInitialKeyValues]);

  useEffect(() => {
    if (keyValueFields && keyValueFields.length > 0) {
      setFields(getInitialFields(keyValueFields));
    }
  }, [keyValueFields]);

  const handleFieldChange = (
    id: string,
    updatedField: Omit<Field, 'id'>
  ): void => {
    const newFields = fields.map(field =>
      field.id === id ? { ...updatedField, id } : field
    );
    setFields(newFields);
    onFieldsChange(newFields);
  };

  const handleAddField = (): void => {
    const newField: Field = {
      id: uuidv4(),
      secretName: '',
      secretValue: ''
    };
    const newFields = [...fields, newField];
    setFields(newFields);
    onFieldsChange(newFields);
  };

  const handleRemoveField = (id: string): void => {
    if (fields.length <= 1) {
      setFields(emptyFields);
      onFieldsChange(emptyFields);
      return;
    }
    const newFields = fields.filter(field => field.id !== id);
    setFields(newFields);
    onFieldsChange(newFields);
  };

  return (
    <Box>
      <Grid item>
        <Grid container spacing={1}>
          <Grid item className={classNames.titleWrapper}>
            <Box>Secret name</Box>
          </Grid>
          <Grid item>
            <Box>Secret value</Box>
          </Grid>
          <Grid item xs={1} className={classNames.removeButton}></Grid>
        </Grid>
      </Grid>
      {fields.map(field => (
        <div key={field.id} className={classNames.fieldWrapper}>
          <KeyValueListItem
            field={field}
            id={field.id}
            onChange={handleFieldChange}
            onRemove={handleRemoveField}
            isRemoveDisabled={false}
            isKeyDisabled={initialIds.includes(field.id)}
          />
        </div>
      ))}
      <Box mt={2}>
        <TextButton
          onClick={handleAddField}
          disabled={hasEmptyField || isMaxReached}
        >
          + Add field
        </TextButton>
      </Box>
    </Box>
  );
};
